import React, {RefObject} from "react";
import {connect, RootStateOrAny} from "react-redux";
import IBaseProps from "../../../../../model/interface/IBaseProps";
import IContentType from "../../../../../model/interface/dataStorage/IContentType";
import INotification from "../../../../../model/interface/dataStorage/INotification";
import {
    Divider,
    Empty,
    Form,
    FormInstance,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Switch,
    Table,
    Typography
} from "antd";
import Utils from "../../../../../utils";
import IField, {FIELD_MODE_RELATION, RELATION_FIELD_TYPES} from "../../../../../model/interface/dataStorage/IField";
import selectors from "../../../../../redux/selectors";
import IRepositoryService from "../../../../../model/interface/IRepositoryService";
import RoutePicker from "../../../../shared/pickers/RoutePicker";
import ContentTypePresenterMentions from "../presenter/ContentTypePresenterMentions";
import {IRestServiceChoiceList} from "../../../../../model/interface/api/IRestServiceChoiceListResponse";
import INotificationTrigger from "../../../../../model/interface/dataStorage/INotificationTrigger";
import Button from "../../../../shared/button/Button";
import {DeleteOutlined, PlusOutlined, SaveOutlined} from "@ant-design/icons";
import ScrollContainer from "../../../../shared/scrollContainer/ScrollContainer";
import ActionPicker from "../../../../shared/pickers/ActionPicker";
import {ColumnsType} from "antd/es/table";
import LocaleText from "../../../settings/dictionary/LocaleText";
import TranslationInput from "../../../../shared/input/TranslationInput";
import EmployeesService from "model/service/company/EmployeesService";
import FieldPicker from "../../../../shared/pickers/FieldPicker";
import FilterTreeBuilder from "../../../view/settings/customFilters/FilterTreeBuilder";
import {isValidCron} from "cron-validator";
import CronSyntax from "../../../../shared/input/CronSyntax";

interface IProps extends IBaseProps {
    contentType: IContentType
    onFinish: (notification?: INotification) => void
    notification?: INotification
    findServiceByClassName: (className: string) => IRepositoryService
    employees: IRestServiceChoiceList
    users: IRestServiceChoiceList
    getText: (code: string, fallBack?: string) => string
}

interface ITrigger {
    uuid: string
    field: string | undefined
    type: string
    weeks: number
    days: number
    hours: number
}

interface IState {
    notification: INotification
    triggers: ITrigger[],
    cronTriggers: { value: string }[],
    options: { label: string, options: { value: string, label: string }[], type: string }[]
    newEmail?: string
}

const WEEK_MINUTES = 10080
const DAY_MINUTES = 1440
const HOUR_MINUTES = 60

const DELAY_AFTER = 'after'
const DELAY_BEFORE = 'before'

class NotificationModal extends React.Component<IProps, IState> {

    formRef: RefObject<FormInstance> = React.createRef()

    constructor(props: IProps) {
        super(props);
        console.log(props.notification)
        this.state = {
            notification: this.props.notification || {
                actions: [],
                recipients: [],
                triggers: [],
                route: undefined,
                uuid: Utils.uuid(),
                title: 'Upozornění',
                text: 'Upozornění na ...'
            } as INotification,
            options: [
                {
                    label: 'Dle vazby',
                    type: 'field',
                    options: this.props.contentType.fields
                            .filter((field: IField) => (field.mode === FIELD_MODE_RELATION && field.targetEntity === EmployeesService.getInstance().getRecordClassName()))
                            .map((field: IField) => ({
                                value: 'field:' + field.uuid,
                                label: props.getText(field.label || field.name)
                            }))
                        || {disabled: true, title: 'Žádné vlastnosti', value: 0}
                },
                {
                    label: 'Dle vnořené vazby',
                    type: 'fieldPath',
                    options: this.props.notification?.recipients.filter(r => r.fieldPath).map((r) => ({
                        value: 'fieldPath:' + r.fieldPath,
                        label: r.fieldPath || ''
                    })) || []
                },
                {
                    label: 'Dle zaměstnance',
                    type: 'employee',
                    options: Object.entries(this.props.employees).map(([uuid, label]) => ({
                        value: 'employee:' + uuid,
                        label: label
                    }))
                },
                {
                    label: 'Dle uživatele',
                    type: 'user',
                    options: Object.entries(this.props.users).map(([uuid, label]) => ({
                        value: 'user:' + uuid,
                        label: label
                    }))
                },
                {
                    label: 'Email',
                    type: 'email',
                    options: this.props.notification?.recipients.filter(r => r.email).map((r) => ({
                        value: 'email:' + r.email,
                        label: r.email || ''
                    })) || []
                }
            ],
            triggers: this.props.notification ? this.buildTriggers(this.props.notification!.triggers) : [],
            cronTriggers: this.props.notification?.cronTriggers?.map(v => ({value: v})) || []
        }
    }

    onNameChange = (email: string) => {
        this.setState({newEmail: email})
    };

    addItem = () => {
        const {newEmail} = this.state;
        newEmail && this.setState(state => ({
            options: [...state.options.filter(o => o.type !== 'email'), {
                ...(state.options.find(o => o.type === 'email') || {
                    label: 'Email',
                    options: [],
                    type: 'email'
                }),
                options: [...state.options.find(o => o.type === 'email')?.options || [], {
                    value: 'email:' + newEmail,
                    label: newEmail
                }]
            }],
            notification: {
                ...state.notification,
                recipients: [...state.notification.recipients, {uuid: Utils.uuid(), type: 'email', email: newEmail}]
            }
        }), () => this.formRef.current?.resetFields(['recipients']));
    };

    addNestedFieldItem = (newNestedField: string) => {
        newNestedField && this.setState(state => ({
            options: [...state.options.filter(o => o.type !== 'fieldPath'), {
                ...(state.options.find(o => o.type === 'fieldPath') || {
                    label: 'Dle vnořené vazby',
                    options: [],
                    type: 'fieldPath'
                }),
                options: [...state.options.find(o => o.type === 'fieldPath')?.options || [], {
                    value: 'fieldPath:' + newNestedField,
                    label: newNestedField
                }]
            }],
            notification: {
                ...state.notification,
                recipients: [...state.notification.recipients, {
                    uuid: Utils.uuid(),
                    type: 'fieldPath',
                    fieldPath: newNestedField
                }]
            }
        }), () => this.formRef.current?.resetFields(['recipients']));
    };

    submit() {
        this.formRef.current?.validateFields().then(values => {
            let notification: INotification = {
                ...this.state.notification,
                ...values,
                recipients: values.recipients.map((recipient: string) => {
                    let [type, identification] = recipient.split(':')
                    return {
                        type,
                        [type]: identification
                    }
                }),
                triggers: this.state.triggers.map(trigger => ({
                    field: trigger.field,
                    uuid: trigger.uuid,
                    delay: (trigger.type === DELAY_BEFORE ? -1 : 1) * (trigger.weeks * WEEK_MINUTES + trigger.days * DAY_MINUTES + trigger.hours * HOUR_MINUTES)
                } as INotificationTrigger))
            }
            this.props.onFinish(notification)
        })
    }

    render() {
        const {contentType, match, history} = this.props
        const {notification, options, newEmail} = this.state
        const formData = {
            ...notification,
            recipients: notification.recipients.map(recipient => {
                switch (recipient.type) {
                    case('employee'):
                        return 'employee:' + recipient.employee
                    case('field'):
                        return 'field:' + recipient.field
                    case('user'):
                        return 'user:' + recipient.user
                    case('email'):
                        return 'email:' + recipient.email
                    case('fieldPath'):
                        return 'fieldPath:' + recipient.fieldPath
                    default:
                        return undefined
                }
            })
        }
        return (
            <Modal bodyStyle={{padding: 0}} title={'Notifikace'} visible={true} onCancel={() => this.props.onFinish()}
                   width={700} okButtonProps={{icon: <SaveOutlined/>}} okText={'Uložit'} onOk={() => this.submit()}
                   cancelButtonProps={{hidden: true}}>
                <ScrollContainer style={{maxHeight: '60vh'}} visibility={"visible"} className={'p-4'}>
                    <Form initialValues={formData} ref={this.formRef}>
                        <Form.Item name={"title"} label={"Titulek"} rules={[{required: true}, {pattern: /[a-z_]/}]}>
                            <TranslationInput uuid={notification.uuid}
                                              code={'configuration.notification.' + notification.uuid}
                                              customInput={(value, triggerSave, onChange, className) =>
                                                  <ContentTypePresenterMentions onSubmit={triggerSave}
                                                                                value={value}
                                                                                onChange={onChange}
                                                                                className={className}
                                                                                contentType={contentType}/>}/>

                        </Form.Item>
                        <Form.Item name={"text"} label={"Text"} rules={[{required: true}, {pattern: /[a-z_]/}]}>
                            <TranslationInput uuid={notification.uuid}
                                              code={'configuration.notification.text.' + notification.uuid}
                                              customInput={(value, triggerSave, onChange, className) =>
                                                  <ContentTypePresenterMentions onSubmit={triggerSave}
                                                                                value={value}
                                                                                onChange={onChange}
                                                                                className={className}
                                                                                rows={3}
                                                                                contentType={contentType}/>}/>
                        </Form.Item>
                        <Form.Item name={"recipients"} label={"Příjemci"}>
                            <Select
                                showSearch
                                filterOption={(input, option) =>
                                    Utils.stringContains(option?.label, input)
                                }
                                dropdownRender={menu => (
                                    <div>
                                        {menu}
                                        <Divider style={{margin: '4px 0'}}/>
                                        <Row wrap={false} justify={'space-between'} align={'middle'}
                                             style={{whiteSpace: 'nowrap'}} className={'p-2'}>
                                            <LocaleText code={'content-type.notification.recipient.add.email'}
                                                        fallback={'Přidat email'}/>
                                            <Input type={'email'} className={'ml-2'} onPressEnter={this.addItem}
                                                   style={{flex: 'auto'}} value={newEmail}
                                                   onChange={(e) => this.onNameChange(e.target.value)}/>

                                        </Row>
                                        <Row wrap={false} justify={'space-between'} align={'middle'}
                                             onMouseDown={(e) => e.stopPropagation()}
                                             style={{whiteSpace: 'nowrap'}} className={'p-2'}>
                                            <LocaleText code={'content-type.notification.recipient.add.nested-field'}
                                                        fallback={'Přidat vnořenú vazbu'}/>
                                            <FieldPicker className={'w-100 ml-2'}
                                                         serviceClassName={contentType.fullClassName} mode={'tree'}
                                                         allowTypes={RELATION_FIELD_TYPES} showDisabledTypes={true}
                                                         allowTargetEntity={[EmployeesService.getInstance().getRecordClassName(), EmployeesService.getInstance().getSlaveContentType()?.fullClassName || '']}
                                                         output={'name'}
                                                         onChange={(e) => typeof e === "string" && this.addNestedFieldItem(e)}/>
                                        </Row>
                                    </div>
                                )}
                                mode={"multiple"} options={options}/>
                        </Form.Item>
                        <Form.Item name={"ignoreAuthor"}
                                   label={<LocaleText code={'configuration.notification.ignoreAuthor'}
                                                      fallback={"Vynechat osobu, která akci spustila"}/>}
                                   valuePropName={'checked'}>
                            <Switch/>
                        </Form.Item>
                        <Form.Item name={"route"}
                                   label={<LocaleText code={'configuration.notification.link'} fallback={"Odkaz"}/>}>
                            <RoutePicker canAdd={false}/>
                        </Form.Item>
                        <Form.Item name={"actions"} label={"Akce"}>
                            <ActionPicker contentTypeUuid={contentType.uuid} multiple={true}/>
                        </Form.Item>
                        <Form.Item name={"conditions"} label={<LocaleText code={'configuration.notification.conditions'}
                                                                       fallback={'Podmínky zaslání'}/>}>
                            <FilterTreeBuilder match={match} history={history}
                                               contentTypeFullClassName={contentType.fullClassName} prefix={'notification_conditions_'}/>
                        </Form.Item>
                        <div className="d-flex align-items-center justify-content-between">
                            <Typography.Title level={4}><LocaleText code={'configuration.notification.cron-triggers'}
                                                                    fallback={'Cron spouštěče'}/></Typography.Title>
                            <Button size={"small"} type={"info"} onClick={() => this.addCronTrigger()}
                                    icon={<PlusOutlined/>}/>
                        </div>
                        <Table
                            pagination={false}
                            size={"small"}
                            dataSource={[...this.state.cronTriggers]}
                            columns={this.buildCronTriggerColumns()}
                        />
                        <div className="d-flex align-items-center justify-content-between mt-3">
                            <Typography.Title level={4}><LocaleText code={'configuration.notification.triggers'}
                                                                    fallback={'Spouštěče'}/></Typography.Title>
                            <Button size={"small"} type={"info"} onClick={() => this.addTrigger()}
                                    icon={<PlusOutlined/>}/>
                        </div>
                        {this.buildFieldOptions().length > 0 ? (
                            <Table
                                pagination={false}
                                size={"small"}
                                dataSource={[...this.state.triggers]}
                                columns={this.buildTriggerColumns()}
                            />
                        ) : (
                            <Empty
                                description={this.props.contentType.label + " nemá žádné datové pole pro nastavení spouštěčů"}></Empty>
                        )}
                    </Form>
                </ScrollContainer>

            </Modal>
        )
    }

    addTrigger() {
        const triggers = this.state.triggers
        triggers.push({
            uuid: Utils.uuid(),
            type: DELAY_BEFORE,
            weeks: 0,
            days: 1,
            hours: 0,
            field: undefined
        })
        this.setState({
            triggers
        })
    }

    addCronTrigger() {
        const cronTriggers = this.state.cronTriggers
        cronTriggers.push({
            value: ''
        })
        this.setState({
            cronTriggers
        })
    }

    removeTrigger(uuid: string) {
        const triggers = this.state.triggers
        const index = Utils.findIndex(triggers, {uuid})
        if (index < 0) {
            throw new Error('Cannot delete item ' + uuid + ', index was not found');
        }
        triggers.splice(index, 1)
        this.setState({triggers})
    }

    removeCronTrigger(index: number) {
        const cronTriggers = this.state.cronTriggers
        cronTriggers.splice(index, 1)
        this.setState({cronTriggers}, () => this.formRef.current?.resetFields(['cronTriggers']))
    }

    editTrigger(uuid: string, values: any) {
        const triggers = this.state.triggers
        const index = Utils.findIndex(triggers, {uuid})
        if (index < 0) {
            throw new Error('Cannot edit item ' + uuid + ', index was not found');
        }
        triggers[index] = {...triggers[index], ...values} as ITrigger
        this.setState({triggers})
    }

    buildTriggerColumns(): ColumnsType<ITrigger> {
        const {contentType} = this.props

        return [
            {
                title: <LocaleText code={'configuration.notification.trigger.field'} fallback={'Pole'}/>,
                dataIndex: 'field',
                key: 'field',
                render: (fieldUuid, row) => (
                    <FieldPicker value={fieldUuid} serviceClassName={contentType.fullClassName} output={'uuid'}
                            onChange={(field) => this.editTrigger(row.uuid, {field})}/>)
            }, {
                title: <LocaleText code={'configuration.notification.trigger.type'} fallback={'Typ'}/>,
                dataIndex: 'type',
                render: (type, row) => (
                    <Select value={type} options={[
                        {label: 'Před', value: DELAY_BEFORE},
                        {label: 'Po', value: DELAY_AFTER}
                    ]} onChange={(type: string) => this.editTrigger(row.uuid, {type})}/>)
            }, {
                title: <LocaleText code={'configuration.notification.trigger.weeks'} fallback={'Týdnů'}/>,
                dataIndex: 'weeks',
                render: (value: number, row: ITrigger) => (
                    <InputNumber style={{width: 46}} value={value}
                                 onChange={(weeks: number) => this.editTrigger(row.uuid, {weeks})}/>)
            }, {
                title: <LocaleText code={'configuration.notification.trigger.days'} fallback={'Dní'}/>,
                dataIndex: 'days',
                render: (value: number, row: ITrigger) => (
                    <InputNumber style={{width: 46}} value={value}
                                 onChange={(days: number) => this.editTrigger(row.uuid, {days})}/>)
            }, {
                title: <LocaleText code={'configuration.notification.trigger.hours'} fallback={'Hodin'}/>,
                dataIndex: 'hours',
                render: (value: number, row: ITrigger) => (
                    <InputNumber style={{width: 46}} value={value}
                                 onChange={(hours: number) => this.editTrigger(row.uuid, {hours})}/>)
            },
            {
                title: '',
                render: (row: ITrigger) => (
                    <Row justify={"end"}>
                        <Button size={"small"} type={"danger"} icon={<DeleteOutlined/>}
                                onClick={() => this.removeTrigger(row.uuid)}/>
                    </Row>)
            }]
    }

    buildCronTriggerColumns(): ColumnsType<{ value: string }> {
        return [
            {
                title: <LocaleText code={'configuration.notification.cron-trigger.value'} fallback={'Výraz'}/>,
                dataIndex: 'cron',
                key: 'cron',
                render: (_, __, index) => (
                    <><Form.Item className={'mb-0'} name={['cronTriggers', index]}
                                 rules={[
                                     {required: true, message: "Toto pole je povinné"},
                                     {
                                         validator(_, value) {
                                             if (isValidCron(value)) {
                                                 return Promise.resolve();
                                             }
                                             return Promise.reject('Zadaný text není platný Cron výraz! Prosím upravte dle cron pravidel');
                                         },
                                     },
                                 ]}>
                        <CronSyntax/>
                    </Form.Item>
                    </>)
            },
            {
                title: '',
                render: (_, __, index) => (
                    <Row justify={"end"}>
                        <Button size={"small"} type={"danger"} icon={<DeleteOutlined/>}
                                onClick={() => this.removeCronTrigger(index)}/>
                    </Row>)
            }]
    }

    buildFieldOptions() {
        return this.props.contentType.fields
            .filter(field => field.type.toLowerCase().indexOf('date') >= 0)
            .map(field => ({label: field.label || field.name, value: field.uuid}))
    }

    buildTriggers(triggers: INotificationTrigger[]) {
        if (!triggers) {
            return []
        }
        return triggers.map(trigger => {
            let delay = Math.abs(trigger.delay)
            let weeks = Math.floor(delay / WEEK_MINUTES)
            delay = delay - weeks * WEEK_MINUTES
            let days = Math.floor(delay / DAY_MINUTES)
            delay = delay - days * DAY_MINUTES
            return {
                type: trigger.delay < 0 ? DELAY_BEFORE : DELAY_AFTER,
                field: trigger.field,
                uuid: trigger.uuid,
                weeks,
                days,
                hours: Math.floor(delay / HOUR_MINUTES),
            } as ITrigger
        })
    }
}

const mapStateToProps = (state: RootStateOrAny) => {
    return {
        findServiceByClassName: (className: string) => selectors.services.findOneByFullClassName(state, className),
        getText: (code: string, fallback?: string) => selectors.dictionary.getMessage(state, code, fallback)
    }
}

export default connect(mapStateToProps)(NotificationModal)